import { Injectable } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, shareReplay } from 'rxjs';
import { FormGroupControls } from 'src/app/interfaces/form-and-field.interface';

// TODO: get rid of this service. Consider to use the pristine property of form/control to determine that the form was changed
// A control/form is pristine if the user has not yet changed the value in the UI.

@Injectable({
  providedIn: 'root',
})
export class FormValueService {
  private savedFormValue$: BehaviorSubject<unknown> = new BehaviorSubject(null);

  // eslint-disable-next-line
  public get valueChanged(): Observable<any> {
    return this.savedFormValue$.asObservable();
  }

  public saveFormValue<T>(value: T): void {
    this.savedFormValue$.next(value);
  }

  public formChangeDetection(value: object, startValue: object): boolean {
    if (value && startValue) {
      return !isEqual(value, startValue);
    } else {
      return false;
    }
  }
}

export function isFormChanged<TFormModel extends object>(
  form: Readonly<FormGroupControls<TFormModel>>,
  excludeFields?: Array<keyof TFormModel>,
): Observable<boolean> {
  const _initialValues = filterExcludedFields(form.getRawValue() as TFormModel, excludeFields);

  return new Observable<boolean>((observer) => {
    const currentFormValue = filterExcludedFields(form.getRawValue() as TFormModel, excludeFields);
    const hasChanges = !isEqual(_initialValues, currentFormValue);

    observer.next(hasChanges);

    const subscription = form.valueChanges.subscribe({
      next: (currentValues) => {
        const filteredValues = filterExcludedFields(currentValues as TFormModel, excludeFields);
        const hasChanges = !isEqual(_initialValues, filteredValues);

        observer.next(hasChanges);
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });

    return () => {
      subscription.unsubscribe();
    };
  }).pipe(shareReplay({ bufferSize: 1, refCount: false }));
}

function filterExcludedFields<TFormModel extends object>(
  values: TFormModel,
  excludeFields?: Array<keyof TFormModel>,
): TFormModel {
  if (!excludeFields || excludeFields.length === 0) {
    return structuredClone(values);
  }

  const clonedValues = structuredClone(values);
  excludeFields.forEach((field) => {
    delete clonedValues[field];
  });

  return clonedValues;
}
